import { adminApiAxios } from '@/api'
import { keyBy } from 'lodash'
import type { DateTime } from 'luxon'

import {
  receiveUserDocuments,
  destroyUserDocument,
  receiveSingleUserDocument,
  UserDocument,
} from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import { fetchWrapper } from '../../reducers/fetch'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { StatementData } from '@/components/documents/upload/StatementUploadSection'
import {
  TaxUserDocument,
  destroySingleTaxUserDocument,
} from '@/features/taxes/annual-taxes/taxUserDocuments.slice'

export const adminCreateUserDocument = (data: {
  awsFileName: string
  fileDescription: string
  userId: number
  transactionId: number | null | undefined
  documentType: string
  statementMonth: DateTime | null
  statementFinancialAccountId: number | null
  userDocumentCategoryId: number | null
  statement?: StatementData
  year?: string
  providerId?: number | null
}) =>
  fetchWrapper({
    defaultErrorMessage: 'There was an error creating the user document.',
    defaultValue: null,
    fetchFunction: (dispatch) =>
      adminApiAxios
        .post<{
          financialProfileId: number
          documents: UserDocument
          taxUserDocument?: TaxUserDocument
        }>('/finances/api/v1/admin/user_documents', {
          ...data,
          statementMonth: data.statementMonth?.toFormat(
            DATE_FORMATS_LUXON.YEAR_MONTH
          ),
        })
        .then((json) => {
          dispatch(
            receiveSingleUserDocument({
              userDocument: json.data.documents,
              taxUserDocument: json.data.taxUserDocument,
            })
          )
          return json.data.documents
        }),
  })

export const REQUEST_ADMIN_USER_DOCUMENTS_KEY = (userId: number | string) =>
  `REQUEST_ADMIN_USER_DOCUMENTS_KEY_${userId}`
export const fetchAdminUsersDocuments = (userId: number | string) =>
  fetchWrapper({
    fetchKey: REQUEST_ADMIN_USER_DOCUMENTS_KEY(userId),
    defaultErrorMessage: 'There was an error fetching all user documents.',
    fetchFunction: (dispatch) =>
      adminApiAxios
        .get<{
          documents: UserDocument[]
          financialProfileId: number
        }>(`/finances/api/v1/admin/user_documents/${userId}`)
        .then((json) => {
          dispatch(receiveUserDocuments(keyBy(json.data.documents, 'id')))
          return json.data
        }),
  })

export const adminDestroyUserDocument = (documentId: number) =>
  fetchWrapper({
    defaultErrorMessage: 'There was an error creating the user document.',
    fetchFunction: (dispatch) =>
      adminApiAxios
        .delete<{
          userDocumentId: number
          financialProfileId: number
          taxDocumentId: number | null
          deletedTaxUserDocumentId: number | null
        }>(`/finances/api/v1/admin/user_documents/${documentId}`)
        .then((json) => {
          dispatch(
            destroyUserDocument({
              deletedId: json.data.userDocumentId,
              deletedTaxDocumentId: json.data.taxDocumentId,
              deletedTaxUserDocumentId: json.data.deletedTaxUserDocumentId,
            })
          )
          return json.data
        }),
  })

export const adminUpdateUserDocument = (
  documentId: number,
  data: Partial<UserDocument> & { year?: string | null }
) =>
  fetchWrapper({
    fetchFunction: async (dispatch) => {
      const json = await adminApiAxios.put<{
        document: UserDocument
        financialProfileId: number
        taxUserDocument: TaxUserDocument
        deletedTaxUserDocumentId?: number
      }>(`/finances/api/v1/admin/user_documents/${documentId}`, data)

      dispatch(
        receiveSingleUserDocument({
          userDocument: json.data.document,
          taxUserDocument: json.data.taxUserDocument,
        })
      )
      if (json.data.deletedTaxUserDocumentId) {
        dispatch(
          destroySingleTaxUserDocument(json.data.deletedTaxUserDocumentId)
        )
      }

      return json.data
    },
  })

export const adminUpdateStatement = (
  documentId: number,
  data: Partial<UserDocument> & {
    statementFinancialAccounts?: {
      accountsToCreate: number[]
      accountsToDelete: number[]
    }
  }
) =>
  fetchWrapper({
    fetchFunction: async (dispatch) => {
      const json = await adminApiAxios.put<{
        document: UserDocument
        financialProfileId: number
      }>(`/finances/api/v1/admin/user_documents/statement/${documentId}`, data)

      dispatch(receiveSingleUserDocument({ userDocument: json.data.document }))

      return json.data
    },
  })
